import { Box, Paper, Stack, Typography } from "@mui/material";
import CountPill from "../CountPill";
import { FetcherDetailRow } from "../helpers/fetcherCatalogue";

interface Props {
  /** "This interface" */
  ownLabel: string;
  ownRows: FetcherDetailRow[];
}

const COLUMN_HEADER_SX = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "11px 16px",
  borderBottom: "1px solid #0000001F",
  backgroundColor: "#FAFAFC",
};

const COLUMN_TITLE_SX = {
  fontSize: "12.5px",
  fontWeight: 700,
  letterSpacing: "0.3px",
  textTransform: "uppercase" as const,
  color: "#9A9AA4",
};

/** The mockup's .uniontag - this field_type accumulates across levels. */
const UnionTag = () => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      backgroundColor: "#EDE9F6",
      color: "#5B54C9",
      borderRadius: "3px",
      padding: "0 5px",
      fontSize: "9.5px",
      fontWeight: 700,
      letterSpacing: "0.3px",
      marginLeft: "5px",
    }}
  >
    UNION
  </Box>
);

const ObjectRows = ({
  rows,
  showObjectId,
}: {
  rows: FetcherDetailRow[];
  showObjectId: boolean;
}) => {
  if (rows.length === 0) {
    return (
      <Typography
        sx={{
          fontSize: "12.5px",
          color: "#00000099",
          textAlign: "center",
          py: 3,
        }}
      >
        No objects.
      </Typography>
    );
  }

  const countOfKey = (fieldType: string | null) =>
    rows.filter((row) => row.field_type === fieldType).length;

  return (
    <Box>
      {rows.map((row, index) => {
        const isUnion = Number(row.is_union_key) === 1;
        // is_effective=0 means the collector drops this row - the query says
        // why, and that reason replaces the value it would have sent
        const isDropped = Number(row.is_effective) === 0;
        const repeated = countOfKey(row.field_type);
        const startsGroup =
          repeated > 1 &&
          (index === 0 || rows[index - 1].field_type !== row.field_type);

        return (
          <Box key={row.object_id ?? `${row.field_type}-${index}`}>
            {startsGroup && (
              <Typography
                sx={{
                  padding: "7px 16px 3px",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.3px",
                  color: "#9A9AA4",
                  backgroundColor: "#FCFCFD",
                  borderBottom: "1px solid #F0F0F3",
                }}
              >
                {row.field_type} &times; {repeated}
                {isUnion ? " - all sent" : ""}
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{
                padding: "8px 16px",
                borderBottom: "1px solid #F0F0F3",
                fontSize: "12.5px",
              }}
            >
              <Box sx={{ width: 180, flex: "none" }}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "12.5px",
                    fontWeight: 500,
                  }}
                >
                  {row.field_type}
                </Typography>
                {isUnion && <UnionTag />}
                {Number(row.is_deprecated) === 1 && (
                  <Typography sx={{ fontSize: "11.5px", color: "#C0392B" }}>
                    deprecated
                  </Typography>
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {isDropped ? (
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      color: "#B45309",
                      wordBreak: "break-word",
                    }}
                  >
                    {row.drop_reason || "Not sent"}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "12px",
                      color: "#6E6E78",
                      wordBreak: "break-all",
                    }}
                  >
                    {row.field_value}
                  </Typography>
                )}
              </Box>

              {showObjectId && (
                <Typography
                  sx={{
                    width: 56,
                    flex: "none",
                    fontFamily: "monospace",
                    fontSize: "11px",
                    color: "#9A9AA4",
                    textAlign: "right",
                  }}
                >
                  {row.object_id}
                </Typography>
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * The interface's objects, as the detail query returns them. An interface is
 * the outermost collection level, so nothing sits above it to inherit from and
 * the panel is a single column.
 */
const InterfaceObjectsPanel = ({ ownLabel, ownRows }: Props) => (
  <Paper variant="outlined" sx={{ mt: 2 }}>
    <Typography
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        px: 2,
        py: 1.5,
        borderBottom: "1px solid #0000001F",
      }}
    >
      Objects
    </Typography>

    <Stack direction="row">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={COLUMN_HEADER_SX}>
          <Typography sx={COLUMN_TITLE_SX}>{ownLabel}</Typography>
          <CountPill value={ownRows.length} variant="objects" />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
            editable
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 340, overflowY: "auto" }}>
          <ObjectRows rows={ownRows} showObjectId />
        </Box>
      </Box>
    </Stack>
  </Paper>
);

export default InterfaceObjectsPanel;
